import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/app/AppShell';
import {
  usePolicies,
  useRequests,
  useCreateRequest,
  useCreatePolicy,
  useApproveRequest,
  useRejectRequest,
  useCancelRequest,
  useLeaveBalance,
  useAccrueLeave,
  useOvertimeYear,
  useRecordOvertime,
  type TimeOffRequest,
} from '@/lib/timeOff';
import { useEmployees } from '@/lib/employees';

export function TimeOffPage() {
  const { t } = useTranslation();
  const policies = usePolicies();
  const requests = useRequests();
  const employees = useEmployees();
  const [creating, setCreating] = useState(false);
  const [addingPolicy, setAddingPolicy] = useState(false);

  const requestCount = requests.data?.length ?? 0;
  const policyCount = policies.data?.length ?? 0;

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('timeOff.title', { defaultValue: 'Time off' })}</h1>
            <p className="mt-1 text-sm text-ink-500">
              {requestCount === 1
                ? t('timeOff.summary_one', { defaultValue: '{{requestCount}} request · {{policyCount}} policies seeded across your countries', requestCount, policyCount })
                : t('timeOff.summary_other', { defaultValue: '{{requestCount}} requests · {{policyCount}} policies seeded across your countries', requestCount, policyCount })}
            </p>
          </div>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition"
          >
            {t('timeOff.newRequest', { defaultValue: '+ New request' })}
          </button>
        </div>

        <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl border border-ink-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-100 bg-ink-50 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink-700">{t('timeOff.requests', { defaultValue: 'Requests' })}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-white">
                  <tr className="text-ink-500">
                    <th className="text-start font-medium px-3 py-2">{t('timeOff.colEmployee', { defaultValue: 'Employee' })}</th>
                    <th className="text-start font-medium px-3 py-2">{t('timeOff.colPolicy', { defaultValue: 'Policy' })}</th>
                    <th className="text-start font-medium px-3 py-2">{t('timeOff.colFrom', { defaultValue: 'From' })}</th>
                    <th className="text-start font-medium px-3 py-2">{t('timeOff.colTo', { defaultValue: 'To' })}</th>
                    <th className="text-start font-medium px-3 py-2">{t('timeOff.colStatus', { defaultValue: 'Status' })}</th>
                    <th className="text-end font-medium px-3 py-2">{t('timeOff.colAction', { defaultValue: 'Action' })}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {requests.isLoading && <tr><td colSpan={6} className="text-center text-ink-500 py-8">{t('timeOff.loading', { defaultValue: 'Loading…' })}</td></tr>}
                  {requests.data?.length === 0 && <tr><td colSpan={6} className="text-center text-ink-500 py-8">{t('timeOff.noRequests', { defaultValue: 'No requests yet.' })}</td></tr>}
                  {(requests.data ?? []).map((r) => (
                    <RequestRow
                      key={r.id}
                      r={r}
                      employeeId={r.employeeId}
                      employeeName={employees.data?.find((e) => e.id === r.employeeId)?.legalNameLocal ?? r.employeeId.slice(0, 8)}
                      policyName={policies.data?.find((p) => p.id === r.policyId)?.name ?? r.policyId.slice(0, 8)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <aside className="rounded-2xl border border-ink-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-100 bg-ink-50 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-ink-700">{t('timeOff.policies', { defaultValue: 'Policies' })}</h2>
                <p className="text-[10px] text-ink-500 mt-0.5">{t('timeOff.policiesSubtitle', { defaultValue: 'Statutory floor + your custom leave types' })}</p>
              </div>
              <button onClick={() => setAddingPolicy(true)} className="text-xs font-semibold text-accent-700 hover:text-accent-600">{t('timeOff.add', { defaultValue: '+ Add' })}</button>
            </div>
            <ul className="divide-y divide-ink-100 max-h-[480px] overflow-y-auto">
              {(policies.data ?? []).map((p) => (
                <li key={p.id} className="px-4 py-3">
                  <p className="text-sm font-medium text-ink-900">{p.name}</p>
                  <p className="text-[10px] text-ink-500">{p.countryCode} · {p.kind}</p>
                </li>
              ))}
              {policies.data?.length === 0 && <li className="px-4 py-3 text-xs text-ink-500">{t('timeOff.noPolicies', { defaultValue: 'No policies — add employees in a country to seed.' })}</li>}
            </ul>
          </aside>
        </section>

        {/* Overtime (36協定) Panel */}
        <OvertimePanel />
      </div>

      {creating && <NewRequestDialog onClose={() => setCreating(false)} />}
      {addingPolicy && <NewPolicyDialog onClose={() => setAddingPolicy(false)} />}
    </AppShell>
  );
}

const COUNTRIES = ['JP', 'US', 'GB', 'DE'] as const;
const KINDS = ['paid', 'sick', 'parental', 'care', 'unpaid', 'special'] as const;

function NewPolicyDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreatePolicy();
  const [countryCode, setCountryCode] = useState<string>('JP');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<typeof KINDS[number]>('special');
  const [annualDays, setAnnualDays] = useState('');
  const [maxCarry, setMaxCarry] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const accrualRule: Record<string, unknown> = { source: 'company_policy' };
    if (annualDays.trim()) accrualRule.annual_days = Number(annualDays);
    if (maxCarry.trim()) accrualRule.max_carry = Number(maxCarry);
    try {
      await create.mutateAsync({ countryCode, name: name.trim(), kind, accrualRule });
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setError(Array.isArray(msg) ? msg[0] : (msg || (err as Error).message));
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-md w-full p-6 space-y-4">
        <h2 className="text-lg font-semibold text-ink-900">{t('timeOff.addPolicyTitle', { defaultValue: 'Add a custom leave policy' })}</h2>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-ink-700 mb-1">{t('timeOff.country', { defaultValue: 'Country' })}</span>
            <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none">
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-ink-700 mb-1">{t('timeOff.kind', { defaultValue: 'Kind' })}</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as typeof KINDS[number])} className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none">
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-ink-700 mb-1">{t('timeOff.name', { defaultValue: 'Name' })}<span className="text-warn-500 ms-1">*</span></span>
          <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t('timeOff.namePlaceholder', { defaultValue: 'e.g. Volunteer day / リフレッシュ休暇' })} className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-ink-700 mb-1">{t('timeOff.annualDays', { defaultValue: 'Annual days (optional)' })}</span>
            <input type="number" min={0} value={annualDays} onChange={(e) => setAnnualDays(e.target.value)} className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-ink-700 mb-1">{t('timeOff.maxCarry', { defaultValue: 'Max carry-over (optional)' })}</span>
            <input type="number" min={0} value={maxCarry} onChange={(e) => setMaxCarry(e.target.value)} className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none" />
          </label>
        </div>
        {error && <div className="rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('timeOff.cancel', { defaultValue: 'Cancel' })}</button>
          <button type="submit" disabled={!name.trim() || create.isPending} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition">{create.isPending ? t('timeOff.saving', { defaultValue: 'Saving…' }) : t('timeOff.createPolicy', { defaultValue: 'Create policy' })}</button>
        </div>
      </form>
    </div>
  );
}

function RequestRow({ r, employeeId, employeeName, policyName }: { r: TimeOffRequest; employeeId: string; employeeName: string; policyName: string }) {
  const { t } = useTranslation();
  const approve = useApproveRequest();
  const reject = useRejectRequest();
  const cancel = useCancelRequest();
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  function handleRejectSubmit() {
    const trimmed = rejectReason.trim();
    if (!trimmed) return;
    reject.mutate({ id: r.id, reason: trimmed }, {
      onSuccess: () => {
        setRejecting(false);
        setRejectReason('');
      },
    });
  }

  return (
    <>
      <tr className="hover:bg-ink-50/40">
        <td className="px-3 py-2">
          <Link to={`/app/people/${employeeId}`} className="text-accent-600 hover:text-accent-700 hover:underline font-medium">
            {employeeName}
          </Link>
        </td>
        <td className="px-3 py-2 text-ink-700">{policyName}</td>
        <td className="px-3 py-2 num text-ink-700">{r.startDate.toString().slice(0, 10)}</td>
        <td className="px-3 py-2 num text-ink-700">{r.endDate.toString().slice(0, 10)}</td>
        <td className="px-3 py-2"><StatusPill status={r.status} /></td>
        <td className="px-3 py-2 text-end">
          {r.status === 'pending' ? (
            <div className="flex items-center justify-end gap-1.5">
              <button
                onClick={() => approve.mutate(r.id)}
                disabled={approve.isPending}
                className="px-2 py-1 rounded-md bg-ok-50 text-ok-600 text-xs font-semibold hover:bg-ok-500 hover:text-white transition"
              >
                {t('timeOff.approve', { defaultValue: 'Approve' })}
              </button>
              <button
                onClick={() => setRejecting((v) => !v)}
                disabled={reject.isPending}
                className="px-2 py-1 rounded-md bg-ink-100 text-ink-600 text-xs font-semibold hover:bg-warn-500 hover:text-white transition"
              >
                {t('timeOff.reject', { defaultValue: 'Reject' })}
              </button>
              <button
                onClick={() => cancel.mutate(r.id)}
                disabled={cancel.isPending}
                className="px-2 py-1 rounded-md bg-ink-100 text-ink-500 text-xs font-semibold hover:bg-ink-300 transition"
              >
                {t('timeOff.cancel', { defaultValue: 'Cancel' })}
              </button>
            </div>
          ) : (
            <span className="text-[10px] text-ink-400">—</span>
          )}
        </td>
      </tr>
      {rejecting && (
        <tr className="bg-ink-50/60">
          <td colSpan={6} className="px-3 py-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                autoFocus
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRejectSubmit(); if (e.key === 'Escape') setRejecting(false); }}
                placeholder={t('timeOff.rejectReasonPlaceholder', { defaultValue: 'Reason for rejection…' })}
                className="flex-1 border border-border bg-card rounded-md px-2 py-1 text-xs focus:outline-none focus:border-warn-500"
              />
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectReason.trim() || reject.isPending}
                className="px-2 py-1 rounded-md bg-warn-500 text-white text-xs font-semibold hover:bg-warn-600 disabled:opacity-50 transition"
              >
                {reject.isPending ? t('timeOff.rejecting', { defaultValue: 'Rejecting…' }) : t('timeOff.confirmReject', { defaultValue: 'Confirm reject' })}
              </button>
              <button
                onClick={() => { setRejecting(false); setRejectReason(''); }}
                className="px-2 py-1 rounded-md bg-ink-100 text-ink-500 text-xs font-semibold hover:bg-ink-200 transition"
              >
                {t('timeOff.cancel', { defaultValue: 'Cancel' })}
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function StatusPill({ status }: { status: TimeOffRequest['status'] }) {
  const { t } = useTranslation();
  const map: Record<TimeOffRequest['status'], { cls: string; label: string }> = {
    pending:  { cls: 'bg-ink-100 text-ink-500',       label: `◐ ${t('timeOff.statusPending', { defaultValue: 'Pending' })}` },
    approved: { cls: 'bg-ok-50 text-ok-600 hanko-like', label: `● ${t('timeOff.statusApproved', { defaultValue: 'Approved' })}` },
    rejected: { cls: 'bg-warn-50 text-warn-600',      label: `✕ ${t('timeOff.statusRejected', { defaultValue: 'Rejected' })}` },
    canceled: { cls: 'bg-ink-100 text-ink-400',       label: `— ${t('timeOff.statusCanceled', { defaultValue: 'Canceled' })}` },
  };
  const m = map[status];
  return <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md ${m.cls}`}>{m.label}</span>;
}

function LeaveBalanceBadge({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();
  const balance = useLeaveBalance(employeeId);
  if (!balance.data && !balance.isLoading) return null;
  if (balance.isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs text-ink-500">
        {t('timeOff.loadingLeaveBalance', { defaultValue: 'Loading leave balance…' })}
      </div>
    );
  }
  const b = balance.data!;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 space-y-1">
      <p className="text-[10px] font-semibold text-ink-500 uppercase tracking-wide">
        {t('timeOff.leaveBalance', { defaultValue: 'Leave balance' })}
      </p>
      <div className="flex gap-4 text-sm">
        <span className="text-ink-700">
          <span className="font-semibold text-ink-900">{b.grantedActiveDays}</span>{' '}
          <span className="text-[11px] text-ink-500">{t('timeOff.granted', { defaultValue: 'granted' })}</span>
        </span>
        <span className="text-ink-700">
          <span className="font-semibold text-ink-900">{b.usedDays}</span>{' '}
          <span className="text-[11px] text-ink-500">{t('timeOff.used', { defaultValue: 'used' })}</span>
        </span>
        <span className="text-ink-700">
          <span className="font-semibold text-ok-600">{b.balanceDays}</span>{' '}
          <span className="text-[11px] text-ink-500">{t('timeOff.remaining', { defaultValue: 'remaining' })}</span>
        </span>
      </div>
    </div>
  );
}

function NewRequestDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const employees = useEmployees();
  const policies = usePolicies();
  const create = useCreateRequest();
  const [employeeId, setEmployeeId] = useState<string>('');
  const [policyId, setPolicyId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ employeeId, policyId, startDate, endDate, reason: reason.trim() || undefined });
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
        || (err as { message?: string })?.message
        || 'Could not submit';
      setError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-lg w-full p-6 space-y-4">
        <h2 className="text-lg font-semibold text-ink-900">{t('timeOff.newRequestTitle', { defaultValue: 'New time-off request' })}</h2>
        <Field label={t('timeOff.fieldEmployee', { defaultValue: 'Employee' })} required>
          <select required value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="input">
            <option value="">{t('timeOff.select', { defaultValue: 'Select…' })}</option>
            {(employees.data ?? []).map((e) => (
              <option key={e.id} value={e.id}>{e.legalNameLocal} ({e.countryCode})</option>
            ))}
          </select>
        </Field>
        {employeeId && <LeaveBalanceBadge employeeId={employeeId} />}
        <Field label={t('timeOff.fieldPolicy', { defaultValue: 'Policy' })} required>
          <select required value={policyId} onChange={(e) => setPolicyId(e.target.value)} className="input">
            <option value="">{t('timeOff.select', { defaultValue: 'Select…' })}</option>
            {(policies.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({p.countryCode})</option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('timeOff.fieldStart', { defaultValue: 'Start' })} required><input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input" /></Field>
          <Field label={t('timeOff.fieldEnd', { defaultValue: 'End' })} required><input type="date" required value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input" /></Field>
        </div>
        <Field label={t('timeOff.fieldReason', { defaultValue: 'Reason (optional)' })}>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="input min-h-[60px]" placeholder={t('timeOff.reasonPlaceholder', { defaultValue: '家族の事情、有休消化、その他' })} />
        </Field>
        {error && <div className="rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('timeOff.cancel', { defaultValue: 'Cancel' })}</button>
          <button type="submit" disabled={!employeeId || !policyId || create.isPending} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition">
            {create.isPending ? t('timeOff.saving', { defaultValue: 'Saving…' }) : t('timeOff.submitRequest', { defaultValue: 'Submit request' })}
          </button>
        </div>
      </form>
    </div>
  );
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

function OvertimePanel() {
  const { t } = useTranslation();
  const employees = useEmployees();
  const currentYear = new Date().getFullYear();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [recordingMonth, setRecordingMonth] = useState<number | null>(null);
  const [overtimeHours, setOvertimeHours] = useState('');
  const [workedHours, setWorkedHours] = useState('');
  const [note, setNote] = useState('');
  const [recordError, setRecordError] = useState<string | null>(null);

  const overtimeYear = useOvertimeYear(selectedEmployeeId || null, selectedYear);
  const recordOvertime = useRecordOvertime();
  const accrueLeave = useAccrueLeave();

  const jpEmployees = (employees.data ?? []).filter((e) => e.countryCode === 'JP');
  const allEmployees = employees.data ?? [];

  const yearOptions: number[] = [];
  for (let y = currentYear - 2; y <= currentYear + 1; y++) yearOptions.push(y);

  async function handleRecordOvertime(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedEmployeeId || recordingMonth === null) return;
    setRecordError(null);
    try {
      await recordOvertime.mutateAsync({
        employeeId: selectedEmployeeId,
        year: selectedYear,
        month: recordingMonth,
        overtimeHours: Number(overtimeHours),
        workedHours: workedHours.trim() ? Number(workedHours) : undefined,
        note: note.trim() || undefined,
      });
      setRecordingMonth(null);
      setOvertimeHours('');
      setWorkedHours('');
      setNote('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
        || (err as { message?: string })?.message
        || 'Could not record';
      setRecordError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  const selectedEmployee = allEmployees.find((e) => e.id === selectedEmployeeId);
  const isJpEmployee = selectedEmployee?.countryCode === 'JP';

  return (
    <section className="mt-6 rounded-2xl border border-ink-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-ink-100 bg-ink-50 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-ink-700">
            {t('timeOff.overtimeTitle', { defaultValue: 'Overtime · 36協定' })}
          </h2>
          <p className="text-[10px] text-ink-500 mt-0.5">
            {t('timeOff.overtimeSubtitle', { defaultValue: 'Monthly overtime records and limit violations' })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedEmployeeId}
            onChange={(e) => { setSelectedEmployeeId(e.target.value); setRecordingMonth(null); }}
            className="border border-border bg-card rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-accent-500"
          >
            <option value="">{t('timeOff.selectEmployee', { defaultValue: 'Select employee…' })}</option>
            {allEmployees.map((e) => (
              <option key={e.id} value={e.id}>{e.legalNameLocal} ({e.countryCode})</option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-border bg-card rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-accent-500"
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {isJpEmployee && selectedEmployeeId && (
            <button
              onClick={() => accrueLeave.mutate(selectedEmployeeId)}
              disabled={accrueLeave.isPending}
              className="px-3 py-1.5 rounded-lg bg-accent-50 text-accent-700 border border-accent-200 text-xs font-semibold hover:bg-accent-100 disabled:opacity-50 transition"
            >
              {accrueLeave.isPending ? t('timeOff.accruing', { defaultValue: 'Accruing…' }) : t('timeOff.accrueLeave', { defaultValue: 'Accrue leave (有休付与)' })}
            </button>
          )}
        </div>
      </div>

      {!selectedEmployeeId ? (
        <div className="px-4 py-8 text-center text-sm text-ink-400">
          {t('timeOff.selectEmployeePrompt', { defaultValue: 'Select an employee to view overtime records.' })}
        </div>
      ) : overtimeYear.isLoading ? (
        <div className="px-4 py-8 text-center text-sm text-ink-400">{t('timeOff.loading', { defaultValue: 'Loading…' })}</div>
      ) : (
        <div className="p-4 space-y-4">
          {/* Violations summary */}
          {((overtimeYear.data?.monthlyViolations.length ?? 0) > 0 || (overtimeYear.data?.yearlyViolations.length ?? 0) > 0) && (
            <div className="rounded-lg border border-warn-200 bg-warn-50 px-3 py-2 space-y-1">
              <p className="text-xs font-semibold text-warn-700">
                {t('timeOff.violationsWarnings', { defaultValue: 'Violations / Warnings' })}
              </p>
              {[...(overtimeYear.data?.monthlyViolations ?? []), ...(overtimeYear.data?.yearlyViolations ?? [])].map((v, i) => (
                <p key={i} className={`text-xs ${v.severity === 'violation' ? 'text-warn-700' : 'text-warn-500'}`}>
                  {v.severity === 'violation' ? '✕' : '⚠'} [{v.code}]{v.month != null ? ` ${t('timeOff.monthLabel', { defaultValue: '(month {{month}})', month: v.month })}` : ''} {v.message}
                </p>
              ))}
            </div>
          )}

          {/* Total */}
          {overtimeYear.data && (
            <p className="text-xs text-ink-500">
              {t('timeOff.totalOvertime', { defaultValue: 'Total overtime {{year}}:', year: selectedYear })} <span className="font-semibold text-ink-900">{overtimeYear.data.totalOvertimeHours}h</span>
            </p>
          )}

          {/* Monthly grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {MONTH_NAMES.map((name, i) => {
              const month = i + 1;
              const rec = overtimeYear.data?.records.find((r) => r.month === month);
              const isRecording = recordingMonth === month;
              return (
                <div
                  key={month}
                  className={`rounded-lg border text-center px-2 py-2 cursor-pointer transition ${isRecording ? 'border-accent-400 bg-accent-50' : 'border-border bg-card hover:border-accent-300'}`}
                  onClick={() => { setRecordingMonth(isRecording ? null : month); setRecordError(null); }}
                >
                  <p className="text-[10px] font-medium text-ink-500">{name}</p>
                  {rec ? (
                    <>
                      <p className="text-sm font-semibold text-ink-900">{rec.overtimeHours}h</p>
                      {rec.workedHours != null && <p className="text-[10px] text-ink-400">{t('timeOff.hoursWorked', { defaultValue: '{{hours}}h worked', hours: rec.workedHours })}</p>}
                    </>
                  ) : (
                    <p className="text-xs text-ink-300 mt-0.5">—</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Inline record form */}
          {recordingMonth !== null && (
            <form onSubmit={handleRecordOvertime} className="rounded-lg border border-border bg-card p-3 space-y-2">
              <p className="text-xs font-semibold text-ink-700">
                {t('timeOff.recordOvertimeFor', { defaultValue: 'Record overtime for {{month}} {{year}}', month: MONTH_NAMES[recordingMonth - 1], year: selectedYear })}
              </p>
              <div className="flex flex-wrap gap-2 items-end">
                <label className="block">
                  <span className="block text-[10px] font-medium text-ink-500 mb-0.5">
                    {t('timeOff.overtimeHours', { defaultValue: 'Overtime hours' })}<span className="text-warn-500 ms-1">*</span>
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    required
                    value={overtimeHours}
                    onChange={(e) => setOvertimeHours(e.target.value)}
                    className="border border-border bg-white rounded-md px-2 py-1 text-xs w-24 focus:outline-none focus:border-accent-500"
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] font-medium text-ink-500 mb-0.5">{t('timeOff.workedHours', { defaultValue: 'Worked hours' })}</span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={workedHours}
                    onChange={(e) => setWorkedHours(e.target.value)}
                    className="border border-border bg-white rounded-md px-2 py-1 text-xs w-24 focus:outline-none focus:border-accent-500"
                  />
                </label>
                <label className="block flex-1 min-w-[120px]">
                  <span className="block text-[10px] font-medium text-ink-500 mb-0.5">{t('timeOff.note', { defaultValue: 'Note' })}</span>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t('timeOff.notePlaceholder', { defaultValue: 'Optional note…' })}
                    className="border border-border bg-white rounded-md px-2 py-1 text-xs w-full focus:outline-none focus:border-accent-500"
                  />
                </label>
                <button
                  type="submit"
                  disabled={!overtimeHours || recordOvertime.isPending}
                  className="px-3 py-1.5 rounded-md bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 disabled:opacity-50 transition"
                >
                  {recordOvertime.isPending ? t('timeOff.saving', { defaultValue: 'Saving…' }) : t('timeOff.save', { defaultValue: 'Save' })}
                </button>
                <button
                  type="button"
                  onClick={() => { setRecordingMonth(null); setRecordError(null); }}
                  className="px-3 py-1.5 rounded-md bg-ink-100 text-ink-500 text-xs font-semibold hover:bg-ink-200 transition"
                >
                  {t('timeOff.cancel', { defaultValue: 'Cancel' })}
                </button>
              </div>
              {recordError && <p className="text-xs text-warn-600">{recordError}</p>}
            </form>
          )}
        </div>
      )}

      {/* Accrue leave button for JP employees (bottom placement when no employee selected) */}
      {!selectedEmployeeId && jpEmployees.length > 0 && (
        <div className="px-4 py-3 border-t border-ink-100 bg-ink-50 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-ink-500">
            {t('timeOff.accrueAnnualLeavePrompt', { defaultValue: 'Accrue annual leave for a JP employee:' })}
          </span>
          {jpEmployees.slice(0, 5).map((e) => (
            <button
              key={e.id}
              onClick={() => accrueLeave.mutate(e.id)}
              disabled={accrueLeave.isPending}
              className="px-2 py-1 rounded-md bg-accent-50 text-accent-700 border border-accent-200 text-xs font-semibold hover:bg-accent-100 disabled:opacity-50 transition"
            >
              {e.legalNameLocal}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink-700 mb-1">
        {label}
        {required && <span className="text-warn-500 ms-1">*</span>}
      </span>
      {children}
    </label>
  );
}
